import {
  AlignItems,
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Field,
  Form,
  Input,
  Label,
  ReadingOptimized,
  Space,
  Hint,
  FormFieldStatus,
  TextAlign,
  FontWeight,
  PlainLink,
  HoverEffect,
  PlainButton,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { useForm } from "react-hook-form";
import { TemporaryAccessTokenCreateRequestBody } from "@gocardless/api/dashboard/temporary-access-token";

import {
  getInputErrorStatus,
  VALID_OTP_RECOVERY_CODE,
} from "src/utils/inputValidation";
import { ZendeskLink } from "src/components/ui/ZendeskLink/ZendeskLink";

const SUPPORT_RECOVERY_CODE_LINK =
  "https://hub.gocardless.com/s/knowledge-base-documentation?language=en_GB#Recover_your_account";

interface Props {
  submit: (requestBody: TemporaryAccessTokenCreateRequestBody) => void;
  triggerOneTimePassword: () => void;
}

export const RecoveryCode = ({ submit, triggerOneTimePassword }: Props) => {
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      otp_recovery_code: "",
    },
    mode: "onChange",
    shouldUnregister: true,
  });

  const otp_recovery_code = watch("otp_recovery_code");
  const isInvalidCode = errors.otp_recovery_code?.type === "pattern";
  const submitButtonDisabled = !otp_recovery_code || isInvalidCode;

  return (
    <ReadingOptimized alignText={AlignItems.FlexStart}>
      <Form
        data-reading-optimized
        onSubmit={handleSubmit((data) => submit(data))}
      >
        <Field>
          <Label htmlFor="otp_recovery_code">
            <Trans id="sign-in.otp.recovery_code.hint-text">
              Enter the recovery code you downloaded when you first set up
              2-factor authentication
            </Trans>
          </Label>
          <Input
            type="text"
            id="otp_recovery_code"
            autoComplete="off"
            {...register("otp_recovery_code", {
              required: true,
              pattern: VALID_OTP_RECOVERY_CODE,
            })}
            status={getInputErrorStatus(!!isInvalidCode)}
          />
          {errors.otp_recovery_code &&
            errors.otp_recovery_code.type === "pattern" && (
              <Hint status={FormFieldStatus.Danger}>
                <Trans id="sign-in.otp.recovery_code.invalid-text">
                  The code should be 16 letters and numbers long.
                </Trans>
              </Hint>
            )}
        </Field>
        <Space v={1.5} />
        <Box
          width="100%"
          layout="flex"
          flexDirection={["column", "row"]}
          alignItems={[null, AlignItems.Center]}
        >
          <Button
            type="submit"
            variant={ButtonVariant.PrimaryOnLight}
            disabled={submitButtonDisabled}
            size={[ButtonSize.Md, null, ButtonSize.Md, ButtonSize.Lg]}
          >
            <Trans id="Submit"> Submit </Trans>
          </Button>
          <Box gutterH={[0, 1.5]} gutterV={[0.75, 0]} />
          <PlainButton
            onClick={triggerOneTimePassword}
            weight={FontWeight.SemiBold}
            textAlign={TextAlign.Center}
            effect={HoverEffect.TextDecoration}
          >
            <Trans id="Go back">Go back</Trans>
          </PlainButton>
        </Box>
      </Form>
      <Space v={3} />
      <Box
        layout="flex"
        flexDirection="column"
        alignItems={AlignItems.FlexStart}
      >
        <ZendeskLink
          data-tracking-label="Recovery code"
          variant={ButtonVariant.InlineUnderlined}
          formId={134125}
          css={{ fontWeight: 600 }}
        >
          <Trans id="sign-in.otp.recovery_code.support-link">
            Contact Support for help
          </Trans>
        </ZendeskLink>
        <Space v={0.5} />
        <PlainLink
          href={SUPPORT_RECOVERY_CODE_LINK}
          textDecoration="underline"
          effect={HoverEffect.TextDecoration}
          weight={FontWeight.SemiBold}
        >
          <Trans id="sign-in.otp.recovery_code.info-link">
            What are recovery codes?
          </Trans>
        </PlainLink>
      </Box>
    </ReadingOptimized>
  );
};
